/**
 * Execution History Component
 * Displays detailed execution history for an agent with expandable step details
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { cpFetch } from '@/lib/cpClient';

interface ExecutionHistoryProps {
  agentId: string;
  agentName: string;
}

export default function ExecutionHistory({ agentId, agentName }: ExecutionHistoryProps) {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadHistory();
  }, [agentId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await cpFetch(`/api/execution-history/${agentId}?limit=20`);
      const rows = response?.history ?? response?.executions ?? [];
      setHistory(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Failed to load execution history:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (executionId: string) => {
    const newExpanded = new Set(expandedExecutions);
    if (newExpanded.has(executionId)) {
      newExpanded.delete(executionId);
    } else {
      newExpanded.add(executionId);
    }
    setExpandedExecutions(newExpanded);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Execution History</span>
          <Button variant="outline" size="sm" onClick={loadHistory}>
            Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No executions yet</div>
        ) : (
          <div className="space-y-3">
            {history.map((execution: any, idx: number) => {
              const execId = String(execution?.execution_id || execution?._id || idx);
              const metrics = execution?.metrics && typeof execution.metrics === 'object' ? execution.metrics : {};
              const duration = typeof metrics.duration_ms === 'number' ? `${metrics.duration_ms}ms` : 'duration unknown';
              const cost = typeof metrics.cost_usd === 'number' ? `$${metrics.cost_usd.toFixed(4)}` : 'cost unknown';
              const chain = Array.isArray(execution?.trace?.call_chain) ? execution.trace.call_chain : [];
              const executedAt = execution?.executed_at
                ? new Date(execution.executed_at).toLocaleString()
                : 'time unknown';
              return (
              <div key={execId} className="border rounded-lg p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpand(execId)}
                    >
                      {expandedExecutions.has(execId) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </Button>
                    {execution.status === 'success' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium">
                        {executedAt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                        <span>{duration}</span>
                        <span>{cost}</span>
                        {chain.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            Chain: {chain.join(' → ')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedExecutions.has(execId) && (
                  <div className="mt-3 space-y-2 pl-8">
                    {/* Input */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Input:</p>
                      <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                        {JSON.stringify(execution.input, null, 2)}
                      </pre>
                    </div>

                    {/* Steps */}
                    {Array.isArray(execution.steps) && execution.steps.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          Steps ({execution.steps.length}):
                        </p>
                        <div className="space-y-1">
                          {execution.steps.map((step: any, idx: number) => (
                            <div key={idx} className="text-xs bg-gray-50 p-2 rounded">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">Step {idx + 1}</span>
                                <Badge variant={step.status === 'completed' ? 'default' : 'destructive'}>
                                  {step.status || 'completed'}
                                </Badge>
                              </div>
                              {step.llmOutput && (
                                <p className="mt-1 text-gray-600 truncate">
                                  {step.llmOutput.substring(0, 100)}...
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Output */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-1">Output:</p>
                      <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40">
                        {JSON.stringify(execution.output, null, 2)}
                      </pre>
                    </div>

                    {/* Error */}
                    {execution.error && (
                      <div>
                        <p className="text-xs font-semibold text-red-700 mb-1">Error:</p>
                        <p className="text-xs bg-red-50 p-2 rounded text-red-800">
                          {execution.error}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

